/**
 * Restaurant-scoped destination coordinates. Inert session state only.
 * No browser location APIs, pre-check, checkout payload, or persistence.
 */

import type {
  DestinationCoordinates,
  SetDestinationCoordinatesInput,
} from "@/domain/destination/destination-coordinates.types"

let current: DestinationCoordinates | null = null

function requireRestaurantId(restaurantId: string): string {
  const rid = restaurantId.trim()
  if (!rid) {
    throw new Error("Restaurant id is required")
  }
  return rid
}

function requireFiniteCoordinate(value: number, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Invalid destination ${label}`)
  }
  return value
}

function snapshot(): DestinationCoordinates | null {
  if (!current) return null
  return {
    restaurantId: current.restaurantId,
    latitude: current.latitude,
    longitude: current.longitude,
  }
}

export function setDestinationCoordinates(
  input: SetDestinationCoordinatesInput
): DestinationCoordinates {
  const restaurantId = requireRestaurantId(input.restaurantId)
  const latitude = requireFiniteCoordinate(input.latitude, "latitude")
  const longitude = requireFiniteCoordinate(input.longitude, "longitude")
  current = { restaurantId, latitude, longitude }
  return snapshot() as DestinationCoordinates
}

/**
 * Returns the stored pin only when it belongs to `restaurantId`.
 * Restaurant B never receives Restaurant A's coordinates.
 */
export function getDestinationCoordinates(
  restaurantId: string
): DestinationCoordinates | null {
  const rid = restaurantId.trim()
  if (!rid || !current) return null
  if (current.restaurantId !== rid) return null
  return snapshot()
}

/** Clears only the matching restaurant's pin. No-op if another tenant owns the slot. */
export function clearDestinationCoordinates(restaurantId: string): void {
  const rid = restaurantId.trim()
  if (!rid || !current) return
  if (current.restaurantId === rid) {
    current = null
  }
}

/** Drops the pin for every tenant. Used on restaurant switch, tenant lost, and logout. */
export function clearAllDestinationCoordinates(): void {
  current = null
}

export function resetDestinationCoordinatesStoreForTests(): void {
  current = null
}
